import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { SessionSearchResultResponse } from "../../search/contracts/SessionSearchResultResponse";
import { BookOnBehalfRequest } from "../contracts/BookOnBehalfRequest";
import { OccurrenceSeatResponse } from "../contracts/OccurrenceSeatResponse";
import { PaginatedResult } from "../contracts/PaginatedResult";
import { SeatRequestResponse } from "../contracts/SeatRequestResponse";
import { SeatRequestResponseMapper } from "../mappers/SeatRequestResponseMapper";
import { SeatRequest } from "../models/SeatRequest";

const mapper = new SeatRequestResponseMapper();

/** A seat API error; `code` tells the 409s apart ("full", "duplicate", "status"). */
export interface SeatApiError extends ApiErrorResponse {
  code?: string;
}

const toSeatError = (error: unknown): SeatApiError => {
  const code = axios.isAxiosError(error) ? error.response?.data?.code : undefined;
  return { ...toApiError(error), code: typeof code === "string" ? code : undefined };
};

/** Open booking requests on upcoming dates (first page; the home card shows at most this many). */
export const fetchSeatRequests = createAsyncThunk<
  { items: SeatRequest[]; totalCount: number },
  { page?: number; pageSize?: number } | undefined,
  { rejectValue: SeatApiError }
>("seats/fetchRequests", async (args, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<PaginatedResult<SeatRequestResponse>>("/bookings/seat-requests", {
      params: { page: args?.page ?? 1, pageSize: args?.pageSize ?? 20 },
    });
    return { items: mapper.toSeatRequests(response.data.items), totalCount: response.data.totalCount };
  } catch (error) {
    return rejectWithValue(toSeatError(error));
  }
});

/** Every seat of one Session in [from, to] (server default: the next four weeks). */
export const fetchSessionSeats = createAsyncThunk<
  { bookingId: string; seats: SeatRequest[] },
  { bookingId: string; from?: string; to?: string },
  { rejectValue: SeatApiError }
>("seats/fetchSessionSeats", async ({ bookingId, from, to }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<SeatRequestResponse[]>(`/bookings/${bookingId}/seats`, {
      params: { from, to },
    });
    return { bookingId, seats: mapper.toSeatRequests(response.data) };
  } catch (error) {
    return rejectWithValue(toSeatError(error));
  }
});

/** Requested → Confirmed. */
export const acceptSeat = createAsyncThunk<
  OccurrenceSeatResponse,
  { bookingId: string; seatId: string },
  { rejectValue: SeatApiError }
>("seats/accept", async ({ bookingId, seatId }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<OccurrenceSeatResponse>(
      `/bookings/${bookingId}/seats/${seatId}/accept`,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toSeatError(error));
  }
});

/** Deletes the seat. */
export const declineSeat = createAsyncThunk<
  { bookingId: string; seatId: string },
  { bookingId: string; seatId: string },
  { rejectValue: SeatApiError }
>("seats/decline", async ({ bookingId, seatId }, { rejectWithValue }) => {
  try {
    await axiosInstance.post(`/bookings/${bookingId}/seats/${seatId}/decline`);
    return { bookingId, seatId };
  } catch (error) {
    return rejectWithValue(toSeatError(error));
  }
});

/** Books a member onto one date, Confirmed at once (409 code "full" / "duplicate"). */
export const bookOnBehalf = createAsyncThunk<
  OccurrenceSeatResponse,
  { bookingId: string; request: BookOnBehalfRequest },
  { rejectValue: SeatApiError }
>("seats/bookOnBehalf", async ({ bookingId, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<OccurrenceSeatResponse>(`/bookings/${bookingId}/seats`, request);
    return response.data;
  } catch (error) {
    return rejectWithValue(toSeatError(error));
  }
});

/** Title search over the active Sessions the actor may manage (a plain Instructor's own; all for Owner and Admin), for the on-behalf picker. */
export const searchSessionsToBook = createAsyncThunk<
  SessionSearchResultResponse[],
  string,
  { rejectValue: SeatApiError }
>("seats/searchSessions", async (query, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<SessionSearchResultResponse[]>("/bookings/search", {
      params: { query, limit: 8 },
    });
    return response.data;
  } catch (error) {
    return rejectWithValue(toSeatError(error));
  }
});
